import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, apiError, fmtDate } from "./api";
import { Card, PageHead, Pill, Btn, Input, Textarea, Select, Field, Confirm, Empty } from "./widgets";
import { toast } from "sonner";

const STATUS_TONE = { published: "green", draft: "gray", review: "blue", scheduled: "champagne", archived: "red" };
const CATEGORIES = ["taxes", "business", "education", "protection", "estate", "investments", "immigration", "entrepreneurship", "news"];
const SLOT_OPTIONS = ["hero", "staircase", "marble", "office", "meeting", "leader", "workspace", "laptop"];

// content <-> plain text ("## " prefixes a section heading)
const contentToText = (content = []) =>
  content.map((s) => (s.h ? `## ${s.h}\n` : "") + (s.p || []).join("\n\n")).join("\n\n");

const textToContent = (text) => {
  const blocks = [];
  let cur = null;
  String(text || "").split("\n").forEach((line) => {
    if (line.startsWith("## ")) {
      if (cur) blocks.push(cur);
      cur = { h: line.slice(3).trim(), p: [] };
    } else if (line.trim()) {
      if (!cur) cur = { h: "", p: [] };
      cur.p.push(line.trim());
    }
  });
  if (cur) blocks.push(cur);
  return blocks.filter((b) => b.h || b.p.length);
};

const faqsToText = (faqs = []) => faqs.map((f) => `Q: ${f.q}\nA: ${f.a}`).join("\n\n");
const textToFaqs = (text) => {
  const out = [];
  let cur = null;
  String(text || "").split("\n").forEach((line) => {
    if (line.startsWith("Q: ")) {
      if (cur?.a) out.push(cur);
      cur = { q: line.slice(3).trim(), a: "" };
    } else if (line.startsWith("A: ") && cur) {
      cur.a = line.slice(3).trim();
    }
  });
  if (cur?.a) out.push(cur);
  return out;
};

const BlogManager = () => {
  const [posts, setPosts] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, []);

  const act = async (id, action, payload) => {
    try {
      await api.post(`/admin/blog/${id}/${action}`, payload || {});
      toast.success("Done.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const newArticle = async () => {
    const title = window.prompt("Article title (English):");
    if (!title) return;
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    try {
      const { data } = await api.post("/admin/blog", {
        slug,
        status: "draft",
        category: "education",
        img: "workspace",
        date: new Date().toISOString().slice(0, 10),
        updated: new Date().toISOString().slice(0, 10),
        reviewed: new Date().toISOString().slice(0, 10),
        reading: 4,
        author: { name: "Escarling Martínez", role: { en: "Founder & CEO", es: "Fundadora y CEO" }, bio: { en: "", es: "" } },
        en: { title, excerpt: "", content: [], faqs: [] },
        es: { title: "", excerpt: "", content: [], faqs: [] },
      });
      navigate(`/admin/blog/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!posts) return <p className="text-sm text-[#171717]/50">Loading…</p>;

  return (
    <div data-testid="blog-manager">
      <PageHead
        title="Blog — Aviah Insights"
        sub="Create, edit, schedule and publish bilingual articles."
        actions={<Btn primary onClick={newArticle} data-testid="blog-new">New Article</Btn>}
      />
      {posts.length === 0 ? (
        <Empty>No articles yet.</Empty>
      ) : (
        <>
          {/* Mobile: card list */}
          <div className="space-y-3 lg:hidden">
            {posts.map((p) => (
              <Card key={p.id} data-testid={`blog-card-${p.slug}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link to={`/admin/blog/${p.id}`} className="text-sm text-[#171717] hover:text-[#8a6f3e]">
                      {p.en?.title || p.slug}
                    </Link>
                    <p className="mt-0.5 text-[0.65rem] text-[#171717]/40">/{p.slug} · {p.date}</p>
                  </div>
                  <Pill tone={STATUS_TONE[p.status]}>{p.status}</Pill>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <Btn onClick={() => navigate(`/admin/blog/${p.id}`)} data-testid={`blog-edit-mobile-${p.slug}`}>Edit</Btn>
                  <Btn onClick={() => act(p.id, "duplicate")}>Duplicate</Btn>
                  {p.status !== "published" ? (
                    <Btn onClick={() => act(p.id, "status", { status: "published" })}>Publish</Btn>
                  ) : (
                    <Btn onClick={() => act(p.id, "status", { status: "draft" })}>Unpublish</Btn>
                  )}
                  <Btn onClick={() => act(p.id, "status", { status: "archived" })}>Archive</Btn>
                  <Btn danger className="col-span-2" onClick={() => setConfirmDel(p)}>Delete</Btn>
                </div>
              </Card>
            ))}
          </div>

          {/* Desktop: full table */}
          <Card className="hidden overflow-x-auto p-0 lg:block">
            <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-[#171717]/10 text-left text-[0.6rem] uppercase tracking-[0.16em] text-[#171717]/45">
                <th className="px-5 py-3.5 font-medium">Article</th>
                <th className="px-5 py-3.5 font-medium">Status</th>
                <th className="px-5 py-3.5 font-medium">Date</th>
                <th className="px-5 py-3.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-b border-[#171717]/5 hover:bg-[#F4F0E8]/60" data-testid={`blog-row-${p.slug}`}>
                  <td className="px-5 py-3.5">
                    <Link to={`/admin/blog/${p.id}`} className="text-[#171717] hover:text-[#8a6f3e]">
                      {p.en?.title || p.slug}
                    </Link>
                    <p className="mt-0.5 text-[0.65rem] text-[#171717]/40">/{p.slug}</p>
                  </td>
                  <td className="px-5 py-3.5"><Pill tone={STATUS_TONE[p.status]}>{p.status}</Pill></td>
                  <td className="px-5 py-3.5 text-xs text-[#171717]/55">{p.date}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1.5">
                      <Btn onClick={() => navigate(`/admin/blog/${p.id}`)}>Edit</Btn>
                      <Btn onClick={() => act(p.id, "duplicate")}>Duplicate</Btn>
                      {p.status !== "published" ? (
                        <Btn onClick={() => act(p.id, "status", { status: "published" })}>Publish</Btn>
                      ) : (
                        <Btn onClick={() => act(p.id, "status", { status: "draft" })}>Unpublish</Btn>
                      )}
                      <Btn onClick={() => act(p.id, "status", { status: "archived" })}>Archive</Btn>
                      <Btn danger onClick={() => setConfirmDel(p)}>Delete</Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        </>
      )}
      <Confirm
        open={Boolean(confirmDel)}
        title="Delete this article?"
        body={`"${confirmDel?.en?.title}" will be permanently removed. Consider Archive instead — it keeps the article without showing it publicly.`}
        confirmLabel="Delete permanently"
        onConfirm={async () => {
          try {
            await api.delete(`/admin/blog/${confirmDel.id}`);
            toast.success("Article deleted.");
            setConfirmDel(null);
            load();
          } catch (e) {
            toast.error(apiError(e));
          }
        }}
        onCancel={() => setConfirmDel(null)}
      />
    </div>
  );
};

export const BlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [text, setText] = useState({ en: "", es: "" });
  const [faqText, setFaqText] = useState({ en: "", es: "" });
  const [langTab, setLangTab] = useState("en");
  const [scheduleAt, setScheduleAt] = useState("");
  const [media, setMedia] = useState([]);

  useEffect(() => {
    api.get(`/admin/blog/${id}`).then((r) => {
      setPost(r.data);
      setText({ en: contentToText(r.data.en?.content), es: contentToText(r.data.es?.content) });
      setFaqText({ en: faqsToText(r.data.en?.faqs), es: faqsToText(r.data.es?.faqs) });
    }).catch((e) => toast.error(apiError(e)));
    api.get("/admin/media").then((r) => setMedia(r.data)).catch(() => {});
  }, [id]);

  if (!post) return <p className="text-sm text-[#171717]/50">Loading…</p>;

  const set = (k, v) => setPost({ ...post, [k]: v });
  const setLang = (lang, k, v) => setPost({ ...post, [lang]: { ...post[lang], [k]: v } });

  const save = async (nextStatus) => {
    try {
      const payload = {
        ...post,
        en: { ...post.en, content: textToContent(text.en), faqs: textToFaqs(faqText.en) },
        es: { ...post.es, content: textToContent(text.es), faqs: textToFaqs(faqText.es) },
      };
      await api.put(`/admin/blog/${id}`, payload);
      if (nextStatus) {
        await api.post(`/admin/blog/${id}/status`, nextStatus === "scheduled"
          ? { status: "scheduled", publishAt: scheduleAt || new Date().toISOString() }
          : { status: nextStatus });
      }
      toast.success(nextStatus === "published" ? "Article published — it is live." : "Saved.");
      navigate("/admin/blog");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const L = post[langTab] || {};

  return (
    <div data-testid="blog-editor">
      <PageHead
        title={L.title || post.slug}
        sub={`/${post.slug} · `}
        actions={
          <>
            <Pill tone={STATUS_TONE[post.status]}>{post.status}</Pill>
            <Btn onClick={() => save()} data-testid="blog-save">Save</Btn>
            <Btn primary onClick={() => save("published")} data-testid="blog-publish">Publish</Btn>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        <Card>
          <div className="mb-6 flex gap-2">
            {["en", "es"].map((l) => (
              <button
                key={l}
                onClick={() => setLangTab(l)}
                data-testid={`blog-lang-${l}`}
                className={`rounded-[3px] border px-4 py-2 text-[0.65rem] font-medium uppercase tracking-[0.16em] ${
                  langTab === l ? "border-[#171717] bg-[#171717] text-[#C6A46A]" : "border-[#171717]/15 text-[#171717]/60"
                }`}
              >
                {l === "en" ? "English" : "Español"}
              </button>
            ))}
          </div>

          <div className="space-y-5">
            <Field label="Title">
              <Input value={L.title || ""} onChange={(e) => setLang(langTab, "title", e.target.value)} data-testid="blog-title" />
            </Field>
            <Field label="Excerpt">
              <Textarea rows={2} value={L.excerpt || ""} onChange={(e) => setLang(langTab, "excerpt", e.target.value)} />
            </Field>
            <Field label="Content" hint="Start a line with '## ' for a section heading. Blank lines separate paragraphs.">
              <Textarea
                rows={16}
                value={text[langTab]}
                onChange={(e) => setText({ ...text, [langTab]: e.target.value })}
                data-testid="blog-content"
                className="font-mono text-xs"
              />
            </Field>
            <Field label="FAQs" hint="Pairs of lines: 'Q: question' followed by 'A: answer'.">
              <Textarea
                rows={5}
                value={faqText[langTab]}
                onChange={(e) => setFaqText({ ...faqText, [langTab]: e.target.value })}
                className="font-mono text-xs"
              />
            </Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="SEO Title">
                <Input value={L.seoTitle || ""} onChange={(e) => setLang(langTab, "seoTitle", e.target.value)} />
              </Field>
              <Field label="Meta Description">
                <Input value={L.seoDesc || ""} onChange={(e) => setLang(langTab, "seoDesc", e.target.value)} />
              </Field>
            </div>
          </div>
        </Card>

        <div className="space-y-6">
          <Card>
            <Field label="Hero image">
              <Select value={post.img} onChange={(e) => set("img", e.target.value)} className="w-full" data-testid="blog-image">
                {SLOT_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
                {media.filter((m) => !m.isDemo).map((m) => (
                  <option key={m.id} value={m.url}>{m.title}</option>
                ))}
              </Select>
            </Field>
            <div className="mt-5 space-y-5">
              <Field label="Category">
                <Select value={post.category} onChange={(e) => set("category", e.target.value)} className="w-full">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Field>
              <Field label="Publish date"><Input type="date" value={post.date || ""} onChange={(e) => set("date", e.target.value)} /></Field>
              <Field label="Updated"><Input type="date" value={post.updated || ""} onChange={(e) => set("updated", e.target.value)} /></Field>
              <Field label="Reviewed"><Input type="date" value={post.reviewed || ""} onChange={(e) => set("reviewed", e.target.value)} /></Field>
              <Field label="Reading time (min)"><Input type="number" min="1" value={post.reading || 4} onChange={(e) => set("reading", Number(e.target.value))} /></Field>
            </div>
          </Card>

          <Card>
            <p className="mb-3 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Workflow</p>
            <div className="space-y-2">
              <Btn className="w-full" onClick={() => save("review")}>Send to Review</Btn>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="flex-1" />
                <Btn onClick={() => save("scheduled")}>Schedule</Btn>
              </div>
              <Btn className="w-full" onClick={() => save("archived")}>Archive</Btn>
              <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="block py-2 text-center text-[0.62rem] uppercase tracking-[0.16em] text-[#8a6f3e] hover:underline">
                Preview public page
              </a>
            </div>
          </Card>
        </div>
      </div>

      {/* Mobile sticky action bar — sits just above the admin bottom nav */}
      <div className="fixed inset-x-0 bottom-[calc(61px+env(safe-area-inset-bottom))] z-30 grid grid-cols-2 gap-2 border-t border-[#171717]/10 bg-[#F4F0E8]/95 p-3 backdrop-blur-md lg:hidden" data-testid="blog-mobile-actions">
        <Btn onClick={() => save()} data-testid="blog-save-mobile">Save</Btn>
        <Btn primary onClick={() => save("published")} data-testid="blog-publish-mobile">Publish</Btn>
      </div>
    </div>
  );
};

export default BlogManager;
